<script setup>    
    import { ref, computed } from 'vue';
    import { useRoute, useRouter } from 'vue-router';
    import { useData } from '@/composables/Data';
    import { useBackend } from '@/composables/Backend';
    import { usePriceCheck } from '@/composables/PriceCheck';
    import { useTracking } from '@/composables/Tracking';
    import Renderer from '@/components/Renderer.vue';
    import Spinner from '@/components/Spinner.vue';
    import { useCartman } from '@/composables/Cartman';
    
    const router = useRouter();
    const route = useRoute();
    const data = useData();    
    const backend = useBackend();
    const track = useTracking();
    const priceCheck = usePriceCheck(data);
    const cartman = useCartman();
    const fabricBoltWidth = ref(null);
    const verticalRepeat = ref(null);
    const addTrim = ref(false);
    const comYardageResult = ref(null);

    const steps = Object.entries(data.steps).map((kv) => kv[1]);

    const heading = computed(() => {
        // if product has packages, use the package name
        if (data.currentPackage) {
            return data.currentPackage.value.label;
        }

        // otherwise, default to heading property
        return data.heading;
    });

    const error = ref('');

    const isWaitingOnBackend = ref(false);
    const confirmButtonText = computed(() => {
        if (isWaitingOnBackend.value) {
            return 'Loading...';            
        }
        else {
            return 'Add To Cart →'
        }
    })

    function formatMeasurement(decimal) {
        const wholePart = Math.floor(decimal);
        const fractionPart = decimal - wholePart;
        const fraction = fractionPart ? convertDecimalFractionToFraction(fractionPart) : '';
        return fraction ? `${wholePart} ${fraction}` : `${wholePart}`;
    }

    function convertDecimalFractionToFraction(fractionPart) {
        const gcd = (a, b) => b ? gcd(b, a % b) : a;
        let denominator = 100;  // Supports up to 2 decimal places
        let numerator = fractionPart * denominator;
        const divisor = gcd(numerator, denominator);
        numerator /= divisor;
        denominator /= divisor;
        return `${Math.floor(numerator)}/${Math.floor(denominator)}`;
    }

    function goBack() {
        error.value = '';
        let goToRoute = '';

        if (data.currentPackage && data.currentPackage.value.skipMeasurer) {
            goToRoute = 'configure';
        }
        else {
            goToRoute = 'measure';
        }

        router.push({ name: goToRoute, params: route.params, query: route.query });
    }

    async function submitCOMYardageRequest() {
        try {
            const productType = data.type;
            console.log('productType: ', productType);

            let payload;

            if (productType === 'drapery' || productType === 'cafeCurtains') {
                // Drape-style product
                const rodConstruction =
                    data.steps?.sheers?.selected?.value?.id !== 'none' ? 'double' : 'single';

                const areaToCover =
                    (data.steps?.windowWidth?.rulersMax?.value ?? 0) +
                    (data.steps?.stackWidth?.rulers?.[0]?.current?.value ?? 0) +
                    (data.steps?.stackWidth?.rulers?.[1]?.current?.value ?? 0);

                const panelLength =
                    (data.steps?.windowHeight?.rulersMax?.value ?? 0) +
                    (data.steps?.mountingHeight?.rulersMax?.value ?? 0) -
                    (data.steps?.mountingHeight?.selected?.value?.rulerAdjustment?.current?.value ?? 0);

                payload = {
                    productType,
                    rodConstruction,
                    rodSize: data.steps?.rodSize?.selected?.value?.diameter ?? 'none',
                    mountingHeight: data.steps?.mountingHeight?.rulersMax?.value ?? 0,
                    panelConfiguration: data.steps?.panels?.selected?.value?.id ?? 'center-split',
                    areaToCover,
                    panelLength,
                    draperyFullness: 2, // or dynamic if needed
                    application: 'lead-only',
                    fabricBoltWidth: fabricBoltWidth.value,
                    verticalRepeat: verticalRepeat.value,
                    addTrim: addTrim.value,
                };

            } else if (productType === 'roman-shades') {
                // Shade-style product
                payload = {
                    shadeStyle: data.steps?.style?.selected?.value?.id ?? 'flat',
                    valance: data.steps?.valance?.selected?.value?.id === 'yes',
                    shadeWidth: data.steps?.windowWidth?.rulersMax?.value ?? 0,
                    shadeLength: data.steps?.windowHeight?.rulersMax?.value ?? 0,
                    fabricBoltWidth: fabricBoltWidth.value,
                    verticalRepeat: verticalRepeat.value,
                    addTrim: addTrim.value,
                    application: 'standard',
                    inset: 1.5, // default or dynamic if you add a field
                };
            } else {
                comYardageResult.value = { error: 'Unsupported product type.' };
                return;
            }

            const result = await backend.comYardage(payload);
            comYardageResult.value = result;

        } catch (err) {
            console.error('Error fetching yardage:', err);
            comYardageResult.value = { error: 'Failed to fetch yardage. Please try again.' };
        }
    }

    function confirm() {
        error.value = ''; // reset error in case this the not the first time they're hitting the confirm button        

        if (data.windowName.value == false) {
            error.value = 'Please enter a window name before continuing.';
        }
        else {
            isWaitingOnBackend.value = true;

            // Track the add to cart event - Facebook
            if (typeof window.fbq === 'function') {
                window.fbq('track', 'AddToCart');
            }

            // COM yardage add for description
            if (comYardageResult.value) {
                data.comYardage = { ...comYardageResult.value };
            }

            // the endpoint for editing an item is the same and as long as data.itemId is set then the backend will handle it properly
            backend.addCartItem(data)
            .then(result => {
                if (result == false || result.error) {
                    if (result.error) {
                        error.value = result.error;
                    }
                    else {
                        error.value = 'Sorry, an error occurred attempting to update your shopping cart. Please try again later or contact support.'
                    }
                    console.error(error.value);
                }
                else {
                    track.addToCart(result.cartId, data.type, data.windowName.value, Object.entries(data.steps).map((kv) => kv[1]));
                    const baseUrl = window.location.origin;
                    window.location.href = `${baseUrl}/cart`;
                    // cartman.shouldReload.value = true;
                    cartman.isCartShown.value = true;
                }
            })
            .finally(() => {
                isWaitingOnBackend.value = false;
            });
        }
    }
</script>

<template>
    <div class="container-fluid product-confirm">
        <div class="row product-config">
            <div class="render-wrapper">
                <Renderer></Renderer>
            </div>
            <div class="confirm-wrapper">
                <div class="row product-title">
                    <div class="col product-options__header">
                        <h1 class="confirm-title">{{ heading }}</h1>
                        <h2 class="pretext color--chantilly">Confirm Your Selections</h2>
                    </div>
                </div>
                <div class="row measurements">
                    <div class="col">
                        <div v-if="error">{{ error }}</div>
                        <p>Make installation a breeze by adding a room label below.</p>
                        <input class="text-input__field" type="text" v-model="data.windowName.value" />
                        <div v-for="(total, totalIndex) in data.getTotals()" :key="totalIndex">
                            <template v-for="(measuredValue, valueIndex) in total.values" :key="valueIndex">
                                <template v-if="total.layout == 'formula' || total.layout == 'individual'">                                    
                                    <template v-if="total.layout == 'formula'">
                                        <template v-if="valueIndex > 0 && valueIndex < total.values.length - 1"><div>+</div></template>
                                        <template v-if="valueIndex == total.values.length - 1"><div>=</div></template>
                                    </template>
                                    <div class="measured-value">
                                        <label>{{ measuredValue.label }}</label>
                                        <div>{{ formatMeasurement(measuredValue.getValue()) }}"</div>
                                    </div>
                                </template>
                                <template v-if="total.layout == 'text'">
                                    <p class="confirmer--total-text">{{ measuredValue.label }} = {{ measuredValue.getValue() }}</p>
                                </template>
                            </template>
                        </div>
                        <!-- {{ data }} -->
                    </div>
                </div>
                <div class="row configuration">
                    <div class="col">
                        <template v-for="(step, index) in steps" :key="index">
                            <template v-if="step.showOnConfirmation && (step.disabled == null || step.disabled.value != true)">
                                <div class="row">
                                    <div class="col-5 confirmation-label text-bold">{{ step.name }}:</div>
                                    <div class="col-7 confirmation-label text-bold text-right-aligned"><strong>{{ step.selected.value.label }}</strong></div>
                                </div>
                            </template>
                        </template>
                    </div>
                </div>
                <div v-if="data.steps.fabric.selected.value.id === 'COMAT'" class="row confirm__com">
                    <p>You've selected COM for this product. Please provide the information below to see the yardage required for this product.</p>
                    <BForm class="" style="width: 100%">
                        <div style="margin-bottom: 16px; margin-top: 16px;" class="form__row">                                    
                            <BFormInput class="text-input__field" id="fabricBoltWidth" placeholder="Fabric Bolt Width" size="lg" v-model="fabricBoltWidth" />
                            <BFormInput class="text-input__field" id="verticalRepeat" placeholder="Vertical Repeat" size="lg" v-model="verticalRepeat" />
                        </div>
                        <BFormGroup style="margin-bottom: 16px;">                                    
                            <BFormCheckbox v-model="addTrim">Add Trim?</BFormCheckbox>
                        </BFormGroup>
                        <BButton class="button primary__btn" @click="submitCOMYardageRequest">
                            Calculate Yardage
                        </BButton>
                    </BForm>
                    <div v-if="comYardageResult && !comYardageResult.error" style="width: 100%; margin-top: 32px;">
                        <h5>Yardage Estimate</h5>
                        <ul>
                            <li><strong>Fabric Yardage:</strong> {{ comYardageResult.fabricYardage }}</li>
                            <li><strong>Number of Cuts:</strong> {{ comYardageResult.numOfCuts || comYardageResult.numberOfCuts }}</li>
                            <li v-if="comYardageResult.fabricLength"><strong>Fabric Length:</strong> {{ comYardageResult.fabricLength }}</li>
                            <li v-if="comYardageResult.panelLength"><strong>Panel Length:</strong> {{ comYardageResult.panelLength }}</li>
                            <li><strong>Trim Yardage:</strong> {{ comYardageResult.trimYardage }}</li>
                        </ul>
                    </div>
                    <div v-if="comYardageResult && comYardageResult.error" class="text-danger" style="width: 100%; margin-top: 32px;">
                        {{ comYardageResult.error }}
                    </div>
                </div>
                <div class="row total">
                    <div class="col-4 add-cart__total dark">
                        <span>
                            Total:
                            <template v-if="priceCheck.price.value == priceCheck.salePrice.value">{{ $formatCurrency(priceCheck.salePrice.value) }}</template>
                            <template v-else-if="priceCheck.price.value >= priceCheck.salePrice.value"><span class="pricing--strikeout">{{ $formatCurrency(priceCheck.price.value) }}</span> {{ $formatCurrency(priceCheck.salePrice.value) }}</template>
                            <Spinner v-else></Spinner>                                                
                        </span>
                    </div>
                    <div class="col-4 color--lavaglas">
                        <a @click="goBack">← Back</a>
                    </div>
                    <div class="col-4 add-cart__text color--lavaglas">
                        <a @click="confirm">{{ confirmButtonText }}</a>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>